/**
 * Typed API client for internal chat (Messages) — Epic 218.
 *
 * Direct/group/workgroup conversations the caller is a member of, their
 * messages, reactions and per-member read state. Mirrors internal/chat.
 */

import { apiClient } from '@/lib/apiClient';

export type ChatType = 'direct' | 'group' | 'workgroup' | 'bot';

export interface Conversation {
  id: string;
  chatType: ChatType;
  name: string | null;
  workgroupId: string | null;
  createdAt: string;
  lastReadAt: string | null;
}

export interface ChatMessage {
  id: string;
  senderUserId: string | null;
  body: string | null;
  attachments?: unknown;
  replyToId: string | null;
  editedAt: string | null;
  sentAt: string;
  mentionedUserIds?: string[];
}

export async function listConversations(): Promise<Conversation[]> {
  const { data } = await apiClient.get<Conversation[]>('/chat/chats');
  return data;
}

export interface CreateConversationRequest {
  chatType?: ChatType;
  name?: string;
  workgroupId?: string;
  memberIds?: string[];
}

export async function createConversation(req: CreateConversationRequest): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>('/chat/chats', req);
  return data;
}

// listMessages / sendMessage use the GUARDED endpoints (Epic 226 "utilizable"):
// the backend asserts chat membership before read/write — closing the
// privilege-escalation gap where any authenticated user could read or post to
// any chat — and sendMessage carries @mentions (mentionedUserIds).

export async function listMessages(chatId: string): Promise<ChatMessage[]> {
  const { data } = await apiClient.get<ChatMessage[]>(`/chat-guarded/chats/${chatId}/messages`);
  return data;
}

export async function sendMessage(
  chatId: string,
  body: string,
  replyToId?: string,
  mentionedUserIds?: string[],
): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>(`/chat-guarded/chats/${chatId}/messages`, {
    body,
    replyToId,
    mentionedUserIds,
  });
  return data;
}

export async function reactToMessage(messageId: string, emoji: string): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>(`/chat/messages/${messageId}/react`, {
    emoji,
  });
  return data;
}

export async function markChatRead(chatId: string): Promise<{ ok: boolean }> {
  const { data } = await apiClient.post<{ ok: boolean }>(`/chat/chats/${chatId}/read`, {});
  return data;
}
